import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  Chip,
  Button,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  Divider
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  RequestPage as RequestIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { useValueChainPermissions } from '../hooks/useValueChainPermissions';
import { useAuth } from './auth/AuthProvider';

/**
 * Component that displays value chains filtered by user permissions
 * 
 * This component shows:
 * - Value chains the user has access to
 * - Value chains the user doesn't have access to (with request option)
 * - Current permission status
 */
interface PermissionFilteredValueChainsProps {}

const PermissionFilteredValueChains: React.FC<PermissionFilteredValueChainsProps> = () => {
  const { isAuthenticated, user } = useAuth();
  const {
    allValueChains,
    loading,
    error,
    loadAllValueChains,
    grantAccess
  } = useValueChainPermissions();

  const [requestingAccess, setRequestingAccess] = useState<Record<string, boolean>>({});

  // Load all value chains with permission status
  useEffect(() => {
    if (isAuthenticated && user) {
      loadAllValueChains();
    }
  }, [isAuthenticated, user, loadAllValueChains]);

  const handleRequestAccess = async (valueChainId: string, valueChainName: string) => {
    setRequestingAccess(prev => ({ ...prev, [valueChainId]: true }));
    
    try {
      const result = await grantAccess({
        value_chain_id: valueChainId,
        value_chain_name: valueChainName,
        permission_level: 'read',
        notes: `Access requested by ${user?.email}`
      });
      
      const success = result.success;
      
      if (success) {
        // Success feedback could be shown here
        console.log(`Access requested for ${valueChainName}`);
      }
    } catch (err) {
      console.error('Error requesting access:', err);
    } finally {
      setRequestingAccess(prev => ({ ...prev, [valueChainId]: false }));
    }
  };

  // Note: revoke access is not implemented yet because it requires a permission ID

  if (!isAuthenticated) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper elevation={1} sx={{ p: 3, textAlign: 'center' }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Authentication Required
          </Alert>
          <Typography variant="h6" gutterBottom>
            Please log in to view value chain permissions
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading value chain permissions...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {error}
        </Alert>
      </Box>
    );
  }

  const accessibleChains = allValueChains.filter(chain => chain.has_access);
  const inaccessibleChains = allValueChains.filter(chain => !chain.has_access);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Value Chain Access Control
      </Typography>
      
      <Typography variant="body1" color="text.secondary" paragraph>
        Manage your access to different value chains. You can only view graphs for value chains you have permission to access.
      </Typography>

      {/* Accessible Value Chains */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <CheckCircleIcon color="success" sx={{ mr: 1 }} />
          Accessible Value Chains ({accessibleChains.length})
        </Typography>
        
        {accessibleChains.length === 0 ? (
          <Alert severity="info">
            You don't have access to any value chains yet. Contact your administrator to request access.
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {accessibleChains.map((chain) => (
              <Card key={chain.element_id} sx={{ minWidth: 300, maxWidth: 400 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {chain.name}
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    <Chip
                      label={chain.permission_level || 'read'}
                      color="success"
                      size="small"
                      sx={{ mr: 1 }}
                    />
                    <Chip
                      label="Active"
                      color="success"
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary">
                    <strong>Element ID:</strong> {chain.element_id}
                  </Typography>
                  
                </CardContent>
                
                <CardActions>
                  {/* Revoke access UI intentionally omitted until permission IDs are available */}
                </CardActions>
              </Card>
            ))}
          </Box>
        )}
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Inaccessible Value Chains */}
      <Box>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <LockIcon color="action" sx={{ mr: 1 }} />
          Request Access ({inaccessibleChains.length})
        </Typography>
        
        {inaccessibleChains.length === 0 ? (
          <Alert severity="success">
            You have access to all available value chains!
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {inaccessibleChains.map((chain) => (
              <Card key={chain.element_id} sx={{ minWidth: 300, maxWidth: 400, opacity: 0.7 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {chain.name}
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    <Chip
                      label="No Access"
                      color="default"
                      size="small"
                      sx={{ mr: 1 }}
                    />
                    <Chip
                      label="Request Required"
                      color="warning"
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary">
                    <strong>Element ID:</strong> {chain.element_id}
                  </Typography>
                </CardContent>
                
                <CardActions>
                  <Button
                    variant="contained"
                    startIcon={<RequestIcon />}
                    onClick={() => handleRequestAccess(chain.element_id, chain.name)}
                    disabled={requestingAccess[chain.element_id]}
                    color="primary"
                  >
                    {requestingAccess[chain.element_id] ? 'Requesting...' : 'Request Access'}
                  </Button>
                </CardActions>
              </Card>
            ))}
          </Box>
        )}
      </Box>

      {/* Summary */}
      <Box sx={{ mt: 4 }}>
        <Paper elevation={1} sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Typography variant="h6" gutterBottom>
            Access Summary
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You have access to <strong>{accessibleChains.length}</strong> out of <strong>{allValueChains.length}</strong> value chains.
            {inaccessibleChains.length > 0 && (
              <> You can request access to <strong>{inaccessibleChains.length}</strong> additional value chains.</>
            )}
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
};

export default PermissionFilteredValueChains;
